import { useState, useEffect } from 'react';
import { apiFetch } from '../App';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway', pstbets_bw: 'PST Bets', sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267', bbets_bw: 'BBets', betxplosion_bw: 'BetXplosion',
};

function HeatBar({ value, max }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const color = pct >= 75 ? '#00e5b0' : pct >= 50 ? '#f59e0b' : pct >= 25 ? '#3d8ef8' : '#5a6a82';
  return (
    <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden', marginTop: 6 }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .4s ease' }} />
    </div>
  );
}

function HeatLabel({ count, avgEdge }) {
  if (avgEdge >= 10) return <span style={{ fontSize: 10, color: '#00e5b0' }}>🔥 Hot</span>;
  if (avgEdge >= 6)  return <span style={{ fontSize: 10, color: '#f59e0b' }}>⚡ Warm</span>;
  return <span style={{ fontSize: 10, color: '#5a6a82' }}>· Mild</span>;
}

export default function HeatMap({ onNavigate }) {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('league'); // league | bookmaker | time
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await apiFetch('/value-bets?limit=200');
      setBets(data.bets || []);
      setLastRefresh(new Date());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  // Group by league
  const byLeague = {};
  bets.forEach(b => {
    const key = b.league || 'Unknown';
    if (!byLeague[key]) byLeague[key] = { bets: [], edges: [] };
    byLeague[key].bets.push(b);
    byLeague[key].edges.push(b.edgePct || 0);
  });

  const leagueData = Object.entries(byLeague)
    .map(([league, d]) => ({
      league,
      count: d.bets.length,
      avgEdge: d.edges.reduce((s, e) => s + e, 0) / d.edges.length,
      maxEdge: Math.max(...d.edges),
      bookmakers: [...new Set(d.bets.map(b => b.bookmakeName))],
      selections: { home: 0, draw: 0, away: 0 },
    }))
    .map(d => {
      byLeague[d.league].bets.forEach(b => {
        if (b.selection === 'home') d.selections.home++;
        else if (b.selection === 'draw') d.selections.draw++;
        else if (b.selection === 'away') d.selections.away++;
      });
      return d;
    })
    .sort((a, b) => b.count - a.count || b.avgEdge - a.avgEdge)
    .slice(0, 20);

  // Group by bookmaker
  const byBookmaker = {};
bets.forEach(b => {
  const key = b.bookmakeName || b.bookmaker || 'Unknown';
  if (!byBookmaker[key]) byBookmaker[key] = { bets: [], edges: [] };
  byBookmaker[key].bets.push(b);
  byBookmaker[key].edges.push(b.edgePct || 0);

  // Mirror Advbet trio — Bet267 and BetXplosion share BBets odds
  if (key === 'bbets_bw') {
    ['bet267_bw', 'betxplosion_bw'].forEach(mirror => {
      if (!byBookmaker[mirror]) byBookmaker[mirror] = { bets: [], edges: [] };
      byBookmaker[mirror].bets.push(b);
      byBookmaker[mirror].edges.push(b.edgePct || 0);
    });
  }
});

  const bookmakerData = Object.entries(byBookmaker)
    .map(([bm, d]) => ({
      bookmaker: bm,
      label: BOOKMAKER_LABELS[bm] || bm,
      count: d.bets.length,
      avgEdge: d.edges.reduce((s, e) => s + e, 0) / d.edges.length,
      maxEdge: Math.max(...d.edges),
      leagues: [...new Set(d.bets.map(b => b.league).filter(Boolean))].slice(0, 3),
    }))
    .sort((a, b) => b.count - a.count);

  // Group by kickoff time bucket
  const byTime = { 'Next 3 hours': [], 'Today': [], 'Tomorrow': [], 'Later': [] };
  const now = new Date();
  bets.forEach(b => {
    const ko = new Date(b.commenceTime);
    const hoursAway = (ko - now) / (1000 * 60 * 60);
    if (hoursAway <= 3) byTime['Next 3 hours'].push(b);
    else if (hoursAway <= 24) byTime['Today'].push(b);
    else if (hoursAway <= 48) byTime['Tomorrow'].push(b);
    else byTime['Later'].push(b);
  });

  const timeData = Object.entries(byTime)
    .map(([label, bets]) => ({
      label,
      count: bets.length,
      avgEdge: bets.length ? bets.reduce((s, b) => s + (b.edgePct || 0), 0) / bets.length : 0,
      urgency: label === 'Next 3 hours' ? 3 : label === 'Today' ? 2 : label === 'Tomorrow' ? 1 : 0,
    }))
    .filter(d => d.count > 0);

  const maxLeagueCount = Math.max(...leagueData.map(d => d.count), 1);
  const maxBmCount = Math.max(...bookmakerData.map(d => d.count), 1);

  const [width, setWidth] = useState(window.innerWidth);
useEffect(() => {
  const handler = () => setWidth(window.innerWidth);
  window.addEventListener('resize', handler);
  return () => window.removeEventListener('resize', handler);
}, []);
const isMobile = width < 768;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>
            Where is the value concentrated today? Updated every minute.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastRefresh && <span style={{ fontSize: 11, color: 'var(--text3)' }}>Updated {lastRefresh.toLocaleTimeString()}</span>}
          <button className="btn btn-outline" style={{ fontSize: 11, padding: '5px 12px' }} onClick={load} disabled={loading}>
            {loading ? 'Loading...' : '↺ Refresh'}
          </button>
        </div>
      </div>

      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 12 }}>
  <div className="stat-card" style={{ '--accent-color': 'var(--accent)', cursor: 'pointer' }} onClick={() => onNavigate('valuebets')}>
    <div className="stat-label">Total Value Bets</div>
    <div className="stat-value">{bets.length}</div>
    <div className="stat-change up">↑ Active now</div>
  </div>
  <div className="stat-card" style={{ '--accent-color': 'var(--warning)', cursor: 'pointer' }} onClick={() => onNavigate('valuebets')}>
    <div className="stat-label">Leagues with value</div>
    <div className="stat-value">{leagueData.length}</div>
    <div className="stat-change neutral">→ Today</div>
  </div>
  <div className="stat-card" style={{ '--accent-color': 'var(--success)', cursor: 'pointer' }} onClick={() => onNavigate('valuebets')}>
    <div className="stat-label">Avg Edge</div>
    <div className="stat-value">
      {bets.length ? `+${(bets.reduce((s, b) => s + (b.edgePct || 0), 0) / bets.length).toFixed(1)}%` : '—'}
    </div>
    <div className="stat-change up">Across all bets</div>
  </div>
  <div className="stat-card" style={{ '--accent-color': 'var(--accent2)', cursor: 'pointer' }} onClick={() => onNavigate('valuebets')}>
    <div className="stat-label">Urgent (3hrs)</div>
    <div className="stat-value">{byTime['Next 3 hours'].length}</div>
    <div className="stat-change neutral">→ Kick off soon</div>
  </div>
</div>

      {/* View tabs */}
      <div style={{ display: 'flex', gap: 8 }}>
        {[
          { id: 'league', label: '🏆 By League' },
          { id: 'bookmaker', label: '📚 By Bookmaker' },
          { id: 'time', label: '⏰ By Kick-off' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            className={view === t.id ? 'btn btn-primary' : 'btn btn-outline'}
            style={{ fontSize: 12, padding: '7px 14px' }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* LEAGUE VIEW */}
      {view === 'league' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15 }}>League Heat Map</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Leagues ranked by number of value bets today</div>
          </div>

          {loading ? (
            <div style={{ padding: 20 }}>
              {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: 60, marginBottom: 10 }} />)}
            </div>
          ) : leagueData.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🗺️</div>
              <p>No value bets available right now.</p>
            </div>
          ) : leagueData.map((d, i) => (
            <div key={d.league}
  onClick={() => {
  sessionStorage.setItem('vb_filter_league', d.league);
  onNavigate('valuebets');
}}
  style={{
    padding: '14px 20px',
    borderBottom: '1px solid var(--border)',
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '40px 1fr 120px 100px',
    gap: 12, alignItems: 'center',
    cursor: 'pointer',
    transition: 'background .12s',
  }}
  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
>
              {/* Rank */}
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: i < 3 ? 'var(--accent)' : 'var(--text3)' }}>
                {i + 1}
              </div>

              {/* League info */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{d.league}</span>
                  <HeatLabel count={d.count} avgEdge={d.avgEdge} />
                </div>
                <HeatBar value={d.count} max={maxLeagueCount} />
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                  {d.bookmakers.slice(0, 3).map(b => BOOKMAKER_LABELS[b] || b).join(', ')}
                </div>
              </div>

              {/* Count */}
              <div style={{ textAlign: isMobile ? 'left' : 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, color: 'var(--accent)' }}>{d.count}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>value bets</div>
              </div>

              {/* Avg edge */}
              <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: d.avgEdge >= 8 ? 'var(--success)' : 'var(--accent3)' }}>
                  +{d.avgEdge.toFixed(1)}%
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>avg edge</div>
              </div>
            </div>
          ))}

          <div style={{ padding: '12px 20px', background: 'rgba(61,142,248,0.04)', borderTop: '1px solid var(--border)' }}>
            <button
              onClick={() => onNavigate('valuebets')}
              className="btn btn-outline"
              style={{ fontSize: 12, padding: '6px 14px' }}
            >
              View all value bets →
            </button>
          </div>
        </div>
      )}

      {/* BOOKMAKER VIEW */}
      {view === 'bookmaker' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15 }}>Bookmaker Value Map</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Which bookmakers are offering the most value today</div>
          </div>

          {bookmakerData.map(d => (
            <div key={d.bookmaker}
  onClick={() => {
    sessionStorage.setItem('vb_filter_bookmaker', d.bookmaker);
    onNavigate('valuebets');
  }}
  style={{
    padding: '16px 20px', borderBottom: '1px solid var(--border)',
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 100px 100px',
    gap: 12, alignItems: 'center',
    cursor: 'pointer', transition: 'background .12s',
  }}
  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{d.label}</div>
                <HeatBar value={d.count} max={maxBmCount} />
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                  Top leagues: {d.leagues.join(', ') || '—'}
                </div>
              </div>
              <div style={{ textAlign: isMobile ? 'left' : 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, color: 'var(--accent)' }}>{d.count}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>value bets</div>
              </div>
              <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: d.avgEdge >= 8 ? 'var(--success)' : 'var(--accent3)' }}>
                  +{d.avgEdge.toFixed(1)}%
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>avg edge</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TIME VIEW */}
      {view === 'time' && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap: 16 }}>
          {timeData.map(d => (
            <div key={d.label} className="card" style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{d.label}</div>
                  {d.urgency === 3 && <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>⏱ Act fast</span>}
                  {d.urgency === 2 && <span style={{ fontSize: 11, color: '#f59e0b' }}>Today</span>}
                  {d.urgency === 1 && <span style={{ fontSize: 11, color: 'var(--text3)' }}>Tomorrow</span>}
                  {d.urgency === 0 && <span style={{ fontSize: 11, color: 'var(--text3)' }}>Later this week</span>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 32, color: d.urgency === 3 ? '#ef4444' : 'var(--accent)', lineHeight: 1 }}>
                    {d.count}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>bets</div>
                </div>
              </div>
              <div style={{ height: 1, background: 'var(--border)', marginBottom: 16 }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Avg edge</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18, color: 'var(--accent)' }}>
                    +{d.avgEdge.toFixed(1)}%
                  </div>
                </div>
                <button
                  onClick={() => onNavigate('valuebets')}
                  className="btn btn-outline"
                  style={{ fontSize: 11, padding: '6px 12px', alignSelf: 'flex-end' }}
                >
                  View bets →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tip */}
      <div style={{
        padding: '14px 18px', background: 'rgba(61,142,248,0.05)',
        border: '1px solid rgba(61,142,248,0.15)', borderRadius: 'var(--radius-sm)',
        fontSize: 13, color: 'var(--text3)', lineHeight: 1.6,
      }}>
        💡 <strong style={{ color: 'var(--text2)' }}>How to use the Heat Map:</strong> Leagues with more value bets and higher average edges are the best hunting grounds today. Focus your attention there first, then use the Value Bets page to find the specific bets.
      </div>
    </div>
  );
}